import { PartialEditUserFormRequest, SubjectPerUser, User } from '../../../../../api/interfaces/User';
import { Alert, Autocomplete, Box, CircularProgress, IconButton, ListItemText, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PinnacleTextField } from '../../../../../components/PinnacleTextField';
import { useCallback, useEffect, useState } from 'react';
import { assignSubjectPerUser, getSubjectsByUserId } from '../../../../../api/services/user';
import { handleApiError } from '../../../../../utils/errorHandler';
import { pushNotification } from '../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { NO_DATA_TO_DISPLAY_URL } from '../../../../../utils/constants';

interface AddRemoveSubjectsProps {
    formData: PartialEditUserFormRequest;
    onChange: (field: string, value: any) => void;
    selectedUserInfo: User | null;
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
    disabled?: boolean;
}

const AddRemoveSubjects: React.FC<AddRemoveSubjectsProps> = ({
    formData,
    selectedUserInfo,
    setIsSubmitting,
}) => {
    const [subjectsPerUser, setSubjectsPerUser] = useState<SubjectPerUser[]>([]);
    const [selectedSubjects, setSelectedSubjects] = useState<SubjectPerUser[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isNotAssignedToProgram, setIsNotAssignedToProgram] = useState<boolean>(false);
    const [loadingSubjectId, setLoadingSubjectId] = useState<string | null>(null);

    const fetchSubjectsPerUser = useCallback(async () => {
        try {
            if (selectedUserInfo) {
                const subjectsPerUserResponse = await getSubjectsByUserId(selectedUserInfo._id);
                setSubjectsPerUser(subjectsPerUserResponse.data);

                setSelectedSubjects(subjectsPerUserResponse.data.filter((s: SubjectPerUser) => s.isEnrolled));
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, [selectedUserInfo]);

    const handleAdd = async (subjectId: string) => {
        try {
            setLoadingSubjectId(subjectId);
            if (selectedUserInfo) {
                await assignSubjectPerUser(selectedUserInfo._id, { subjectId, action: 'Add' });

                // Update states
                setSubjectsPerUser(prev =>
                    prev.map(subject =>
                        subject._id === subjectId ? { ...subject, isEnrolled: true } : subject
                    )
                );

                const subjectToAdd = subjectsPerUser.find(subject => subject._id === subjectId);
                if (subjectToAdd) {
                    setSelectedSubjects(prev => [
                        ...prev.filter(item => item._id !== subjectId),
                        subjectToAdd,
                    ]);
                }
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
        } finally {
            setLoadingSubjectId(null);
        }
    };

    const handleRemove = async (subjectId: string) => {
        try {
            setLoadingSubjectId(subjectId);
            if (selectedUserInfo) {
                await assignSubjectPerUser(selectedUserInfo._id, { subjectId, action: 'Remove' });

                // Update states
                setSubjectsPerUser(prev =>
                    prev.map(subject =>
                        subject._id === subjectId ? { ...subject, isEnrolled: false } : subject
                    )
                );
                setSelectedSubjects(prev => prev.filter(item => item._id !== subjectId));
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
        } finally {
            setLoadingSubjectId(null);
        }
    };

    // Initial load
    useEffect(() => {
        const run = async () => {
            try {
                setIsSubmitting(true);
                setLoading(true);

                if (formData.programId) {
                    setIsNotAssignedToProgram(false);
                    await fetchSubjectsPerUser();
                } else {
                    setIsNotAssignedToProgram(true);
                }
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({ details: msg, type: NotificationTypes.Error });
                    },
                });
            } finally {
                setIsSubmitting(false);
                setLoading(false);
            }
        };

        run();
    }, [formData.programId, fetchSubjectsPerUser]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        );
    }
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{ pb: 1 }}>
                <Alert severity="info" icon={false} sx={{ backgroundColor: '#E5F8FE', color: '#26C6F9' }}>
                    <Typography variant="subtitle2">
                        This user is currently
                        <Box component="span" sx={{ fontWeight: 'bold' }}>{selectedUserInfo?.isActive ? ' ACTIVE ' : ' INACTIVE '}</Box>
                        and {selectedUserInfo?.isTrial && 'on'} a
                        <Box component="span" sx={{ fontWeight: 'bold' }}>{selectedUserInfo?.isTrial ? ' TRIAL ' : ' SUBSCRIBER '}</Box>
                        {selectedUserInfo?.isTrial && 'status'}.
                    </Typography>
                </Alert>
            </Box>

            {/* {(subjectsPerUser.length > 0) ? ( */}
            <>
                <Box
                    display="flex"
                    alignItems="left"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                >
                    <Typography variant="body1" sx={{ color: '#333533', fontWeight: '600' }}>
                        Add or Remove a Subjects
                        {isNotAssignedToProgram && (
                            <Tooltip title='Please assign a term and program in order to add or remove a subject.' arrow>
                                <IconButton sx={{ p: 0.5 }}>
                                    <InfoOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Typography>

                    <Typography variant="body1" sx={{ color: '#A8A8A8', fontWeight: '600' }}>
                        2 of 3
                    </Typography>
                </Box>

                {/* Active/Inactive Mode Toggle */}
                <Box display="flex" flexDirection="column">
                    <Autocomplete
                        fullWidth
                        options={subjectsPerUser}
                        getOptionLabel={(option: any) => option.name}
                        clearOnBlur
                        disableClearable
                        autoHighlight
                        freeSolo
                        selectOnFocus={false}
                        value={null as any}
                        onChange={() => { }}
                        disabled={isNotAssignedToProgram}
                        renderInput={(params) => (
                            <PinnacleTextField
                                {...params}
                                placeholder="Search / Enter here"
                                variant="outlined"
                            />
                        )}
                        renderOption={(props, option) => {
                            const { key, ...rest } = props;
                            return (
                                (
                                    <Box
                                        component="li"
                                        key={key}
                                        {...rest}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%',
                                            px: 1,
                                        }}
                                    >
                                        <ListItemText primary={option.name} />
                                        {option.isEnrolled ? (
                                            <Typography variant="body2" color="text.secondary">
                                                added
                                            </Typography>
                                        ) : (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAdd(option._id);
                                                }}
                                                disabled={loadingSubjectId === option._id}
                                            >
                                                {loadingSubjectId === option._id ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    <AddIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        )}
                                    </Box>
                                )
                            )
                        }}
                    />

                    {/* Selected subjectPerUsers Section */}
                    <>
                        <Typography variant="body1" sx={{ color: '#333533', fontWeight: '600', pt: 4, pb: 4 }}>
                            List of Assigned Subjects
                        </Typography>


                        {selectedSubjects.length > 0 ? (
                            <Stack spacing={1}>
                                {selectedSubjects.map((subjectPerUser: any) => (
                                    <Box
                                        key={subjectPerUser._id}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            px: 2,
                                            py: 1,
                                        }}
                                    >
                                        <Typography>{subjectPerUser.name}</Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemove(subjectPerUser._id)}
                                            disabled={loadingSubjectId === subjectPerUser._id}
                                        >
                                            {loadingSubjectId === subjectPerUser._id ? (
                                                <CircularProgress size={16} />
                                            ) : (
                                                <CloseIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    color: '#818181'
                                }}
                            >
                                <Box component="img" src={NO_DATA_TO_DISPLAY_URL} alt="No data" sx={{ width: '300px', mb: 1 }} />
                            </Box>
                        )}
                    </>



                </Box>
            </>

        </Box>
    );
};


export default AddRemoveSubjects;
